'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function FatIntakeCalculator() {
  const t = useTranslations("tools.fatIntakeCalculator");

  const [screenSize, setScreenSize] = useState('mobile');
  const [unitSystem, setUnitSystem] = useState<'imperial' | 'metric'>('imperial');
  const [inputs, setInputs] = useState({
    age: 25,
    gender: 'male' as 'male' | 'female',
    feet: 5,
    inches: 10,
    pounds: 160,
    cm: 178,
    kg: 72,
    activityLevel: 1.375,
  });
  const [results, setResults] = useState<{
    tdee: number;
    minFat: number;
    recFatLow: number;
    recFatHigh: number;
    maxFat: number;
    satFatLimit: number;
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    success: '#10b981',
    successBg: 'rgba(16, 185, 129, 0.1)',
    warning: '#f59e0b',
    warningBg: 'rgba(245, 158, 11, 0.1)',
    error: '#ef4444',
  };

  const baseStyles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative' as const,
      zIndex: 1,
    },
    header: {
      textAlign: 'center' as const,
      marginBottom: '2rem',
    },
    h1: {
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '1rem',
      lineHeight: 1.2,
    },
    summaryText: {
      color: colors.textSecondary,
      lineHeight: 1.6,
    },
    calculatorTool: {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)',
    },
    toolContainer: {
      background: 'white',
      borderRadius: '1rem',
      overflow: 'hidden' as const,
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    },
    toolHeader: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      padding: '2rem',
      textAlign: 'center' as const,
    },
    toolHeaderTitle: {
      margin: 0,
      fontSize: '1.8rem',
      fontWeight: 700,
    },
    calculatorForm: {
      padding: '2rem',
    },
    unitToggle: {
      display: 'flex',
      justifyContent: 'center',
      gap: '1rem',
      marginBottom: '2rem',
      flexWrap: 'wrap' as const,
    },
    unitBtn: {
      padding: '0.6rem 1.5rem',
      border: '2px solid #e5e7eb',
      background: 'white',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontSize: '0.95rem',
      fontWeight: 600,
      transition: 'all 0.3s ease',
    },
    unitBtnActive: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      borderColor: '#2563eb',
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem',
      marginBottom: '1.5rem',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
    },
    label: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.5rem',
      fontSize: '0.95rem',
    },
    input: {
      padding: '0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      outline: 'none',
    },
    select: {
      padding: '0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      outline: 'none',
      cursor: 'pointer',
    },
    heightInputs: {
      display: 'flex',
      gap: '0.75rem',
    },
    genderOptions: {
      display: 'flex',
      gap: '1rem',
      marginTop: '0.5rem',
    },
    genderOption: {
      flex: 1,
    },
    genderLabel: {
      display: 'block',
      padding: '0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '0.5rem',
      textAlign: 'center' as const,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontWeight: 600,
    },
    genderLabelActive: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      borderColor: '#2563eb',
    },
    calculateBtn: {
      width: '100%',
      padding: '1rem',
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontSize: '1.1rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem',
    },
    results: {
      padding: '2rem',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderTop: '3px solid #10b981',
    },
    resultsTitle: {
      margin: '0 0 1.5rem 0',
      color: '#065f46',
      fontSize: '1.5rem',
      textAlign: 'center' as const,
    },
    resultGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    resultCard: {
      background: 'white',
      padding: '1.25rem',
      borderRadius: '0.75rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      textAlign: 'center' as const,
    },
    resultLabel: {
      display: 'block',
      fontSize: '0.85rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
      fontWeight: 600,
    },
    resultValue: {
      display: 'block',
      fontSize: '1.75rem',
      fontWeight: 700,
      color: '#2563eb',
    },
    resultUnit: {
      fontSize: '0.9rem',
      color: '#6b7280',
      fontWeight: 500,
    },
    fatBreakdown: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      marginTop: '1rem',
    },
    fatBreakdownTitle: {
      margin: '0 0 1rem 0',
      color: colors.textPrimary,
      fontSize: '1.1rem',
    },
    fatType: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.75rem',
      marginBottom: '0.5rem',
      background: '#f9fafb',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #2563eb',
    },
    fatTypeName: {
      fontWeight: 600,
      color: colors.textPrimary,
    },
    fatTypeValue: {
      fontWeight: 700,
      color: '#2563eb',
      fontSize: '1.1rem',
    },
    recommendation: {
      background: '#fef3c7',
      borderLeft: '4px solid #f59e0b',
      padding: '1rem',
      borderRadius: '0.5rem',
      marginTop: '1.5rem',
    },
    recommendationTitle: {
      fontWeight: 700,
      color: '#92400e',
      marginBottom: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    recommendationText: {
      color: '#78350f',
      fontSize: '0.95rem',
      lineHeight: 1.6,
    },
    glassCard: {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    h2: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem',
    },
    paragraph: {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem',
    },
    list: {
      paddingLeft: '1.5rem',
      marginBottom: '1rem',
    },
    listItem: {
      marginBottom: '0.75rem',
      lineHeight: 1.6,
      color: colors.textSecondary,
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.5rem',
      fontSize: '1.1rem',
    },
    faqAnswer: {
      color: colors.textSecondary,
      lineHeight: 1.6,
    },
    reference: {
      marginBottom: '0.75rem',
      paddingLeft: '1rem',
      borderLeft: `3px solid ${colors.primary}`,
    },
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      fontWeight: 500,
    },
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let heightCm: number;
    let weightKg: number;

    if (unitSystem === 'imperial') {
      heightCm = (inputs.feet * 30.48) + (inputs.inches * 2.54);
      weightKg = inputs.pounds * 0.453592;
    } else {
      heightCm = inputs.cm;
      weightKg = inputs.kg;
    }

    let bmr: number;
    if (inputs.gender === 'male') {
      bmr = 10 * weightKg + 6.25 * heightCm - 5 * inputs.age + 5;
    } else {
      bmr = 10 * weightKg + 6.25 * heightCm - 5 * inputs.age - 161;
    }

    const tdee = Math.round(bmr * inputs.activityLevel);
    const minFat = Math.round((tdee * 0.20) / 9);
    const recFatLow = Math.round((tdee * 0.25) / 9);
    const recFatHigh = Math.round((tdee * 0.30) / 9);
    const maxFat = Math.round((tdee * 0.35) / 9);
    const satFatLimit = Math.round((tdee * 0.10) / 9);

    setResults({
      tdee,
      minFat,
      recFatLow,
      recFatHigh,
      maxFat,
      satFatLimit,
    });
  };

  const containerStyle = getResponsiveStyle(
    baseStyles.container,
    { padding: '1rem' },
    { padding: '2rem' },
    { padding: '3rem' }
  );

  const headerStyle = getResponsiveStyle(
    baseStyles.header,
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    baseStyles.h1,
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    baseStyles.summaryText,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    baseStyles.calculatorTool,
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 2rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    baseStyles.glassCard,
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    baseStyles.h2,
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    baseStyles.paragraph,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={baseStyles.toolContainer}>
          <div style={baseStyles.toolHeader}>
            <h2 style={baseStyles.toolHeaderTitle}>🥑 {t("toolTitle")}</h2>
          </div>

          <form style={baseStyles.calculatorForm} onSubmit={handleCalculate}>
            <div style={baseStyles.unitToggle}>
              <button
                type="button"
                style={{
                  ...baseStyles.unitBtn,
                  ...(unitSystem === 'imperial' ? baseStyles.unitBtnActive : {}),
                }}
                onClick={() => setUnitSystem('imperial')}
              >
                {t("usUnits")}
              </button>
              <button
                type="button"
                style={{
                  ...baseStyles.unitBtn,
                  ...(unitSystem === 'metric' ? baseStyles.unitBtnActive : {}),
                }}
                onClick={() => setUnitSystem('metric')}
              >
                {t("metricUnits")}
              </button>
            </div>

            <div style={baseStyles.formRow}>
              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label} htmlFor="age">{t("ageLabel")}</label>
                <input
                  style={baseStyles.input}
                  type="number"
                  id="age"
                  min="18"
                  max="80"
                  value={inputs.age}
                  onChange={(e) => setInputs({...inputs, age: parseInt(e.target.value)})}
                  required
                />
              </div>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label}>{t("genderLabel")}</label>
                <div style={baseStyles.genderOptions}>
                  <div style={baseStyles.genderOption}>
                    <input
                      type="radio"
                      id="male"
                      name="gender"
                      value="male"
                      checked={inputs.gender === 'male'}
                      onChange={() => setInputs({...inputs, gender: 'male'})}
                      style={{ position: 'absolute', opacity: 0 }}
                    />
                    <label
                      htmlFor="male"
                      style={{
                        ...baseStyles.genderLabel,
                        ...(inputs.gender === 'male' ? baseStyles.genderLabelActive : {}),
                      }}
                    >
                      {t("male")}
                    </label>
                  </div>
                  <div style={baseStyles.genderOption}>
                    <input
                      type="radio"
                      id="female"
                      name="gender"
                      value="female"
                      checked={inputs.gender === 'female'}
                      onChange={() => setInputs({...inputs, gender: 'female'})}
                      style={{ position: 'absolute', opacity: 0 }}
                    />
                    <label
                      htmlFor="female"
                      style={{
                        ...baseStyles.genderLabel,
                        ...(inputs.gender === 'female' ? baseStyles.genderLabelActive : {}),
                      }}
                    >
                      {t("female")}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {unitSystem === 'imperial' ? (
              <div style={baseStyles.formRow}>
                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label}>{t("heightLabel")}</label>
                  <div style={baseStyles.heightInputs}>
                    <input
                      style={{...baseStyles.input, flex: 1}}
                      type="number"
                      min="3"
                      max="8"
                      value={inputs.feet}
                      onChange={(e) => setInputs({...inputs, feet: parseInt(e.target.value)})}
                      placeholder={t("feetPlaceholder")}
                    />
                    <input
                      style={{...baseStyles.input, flex: 1}}
                      type="number"
                      min="0"
                      max="11"
                      value={inputs.inches}
                      onChange={(e) => setInputs({...inputs, inches: parseInt(e.target.value)})}
                      placeholder={t("inchesPlaceholder")}
                    />
                  </div>
                </div>

                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label} htmlFor="pounds">{t("weightPoundsLabel")}</label>
                  <input
                    style={baseStyles.input}
                    type="number"
                    id="pounds"
                    min="50"
                    max="500"
                    step="0.1"
                    value={inputs.pounds}
                    onChange={(e) => setInputs({...inputs, pounds: parseFloat(e.target.value)})}
                  />
                </div>
              </div>
            ) : (
              <div style={baseStyles.formRow}>
                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label} htmlFor="cm">{t("heightCmLabel")}</label>
                  <input
                    style={baseStyles.input}
                    type="number"
                    id="cm"
                    min="100"
                    max="250"
                    step="0.1"
                    value={inputs.cm}
                    onChange={(e) => setInputs({...inputs, cm: parseFloat(e.target.value)})}
                  />
                </div>

                <div style={baseStyles.formGroup}>
                  <label style={baseStyles.label} htmlFor="kg">{t("weightKgLabel")}</label>
                  <input
                    style={baseStyles.input}
                    type="number"
                    id="kg"
                    min="30"
                    max="300"
                    step="0.1"
                    value={inputs.kg}
                    onChange={(e) => setInputs({...inputs, kg: parseFloat(e.target.value)})}
                  />
                </div>
              </div>
            )}

            <div style={baseStyles.formRow}>
              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label} htmlFor="activity">{t("activityLabel")}</label>
                <select
                  style={baseStyles.select}
                  id="activity"
                  value={inputs.activityLevel}
                  onChange={(e) => setInputs({...inputs, activityLevel: parseFloat(e.target.value)})}
                  required
                >
                  <option value="1.2">{t("sedentary")}</option>
                  <option value="1.375">{t("light")}</option>
                  <option value="1.465">{t("moderate")}</option>
                  <option value="1.55">{t("active")}</option>
                  <option value="1.725">{t("veryActive")}</option>
                  <option value="1.9">{t("extraActive")}</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              style={baseStyles.calculateBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 20px rgba(245, 158, 11, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t("calculateButton")}
            </button>
          </form>

          {results && (
            <div style={baseStyles.results}>
              <h3 style={baseStyles.resultsTitle}>{t("resultsTitle")}</h3>

              <div style={baseStyles.resultGrid}>
                <div style={baseStyles.resultCard}>
                  <span style={baseStyles.resultLabel}>{t("dailyCalories")}</span>
                  <span style={baseStyles.resultValue}>{results.tdee.toLocaleString()}</span>
                  <span style={baseStyles.resultUnit}>{t("kcal")}</span>
                </div>

                <div style={baseStyles.resultCard}>
                  <span style={baseStyles.resultLabel}>{t("totalFat")}</span>
                  <span style={baseStyles.resultValue}>{results.recFatLow}-{results.recFatHigh}</span>
                  <span style={baseStyles.resultUnit}>{t("gramsPerDay")}</span>
                </div>

                <div style={baseStyles.resultCard}>
                  <span style={baseStyles.resultLabel}>{t("saturatedFatMax")}</span>
                  <span style={baseStyles.resultValue}>{results.satFatLimit}</span>
                  <span style={baseStyles.resultUnit}>{t("gramsPerDay")}</span>
                </div>
              </div>

              <div style={baseStyles.fatBreakdown}>
                <h4 style={baseStyles.fatBreakdownTitle}>{t("detailedBreakdown")}</h4>

                <div style={baseStyles.fatType}>
                  <span style={baseStyles.fatTypeName}>{t("minimumFat")}</span>
                  <span style={baseStyles.fatTypeValue}>{results.minFat}g</span>
                </div>

                <div style={{...baseStyles.fatType, borderLeft: '4px solid #10b981'}}>
                  <span style={baseStyles.fatTypeName}>{t("recommendedFat")}</span>
                  <span style={baseStyles.fatTypeValue}>{results.recFatLow}-{results.recFatHigh}g</span>
                </div>

                <div style={baseStyles.fatType}>
                  <span style={baseStyles.fatTypeName}>{t("maximumFat")}</span>
                  <span style={baseStyles.fatTypeValue}>{results.maxFat}g</span>
                </div>

                <div style={{...baseStyles.fatType, borderLeft: '4px solid #ef4444'}}>
                  <span style={baseStyles.fatTypeName}>{t("saturatedFatLimit")}</span>
                  <span style={baseStyles.fatTypeValue}>{results.satFatLimit}g</span>
                </div>
              </div>

              <div style={baseStyles.recommendation}>
                <div style={baseStyles.recommendationTitle}>💡 {t("recommendationsTitle")}</div>
                <div style={baseStyles.recommendationText}>
                  {t("recommendationText", {
                    tdee: results.tdee.toLocaleString(),
                    recFatLow: results.recFatLow,
                    recFatHigh: results.recFatHigh,
                    satFatLimit: results.satFatLimit,
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={baseStyles.list}>
          <li style={baseStyles.listItem}>{t("howToStep1")}</li>
          <li style={baseStyles.listItem}>{t("howToStep2")}</li>
          <li style={baseStyles.listItem}>{t("howToStep3")}</li>
          <li style={baseStyles.listItem}>{t("howToStep4")}</li>
          <li style={baseStyles.listItem}>{t("howToStep5")}</li>
        </ol>
      </section>

      {/* Latest Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>{t("insight1")}</li>
          <li style={baseStyles.listItem}>{t("insight2")}</li>
          <li style={baseStyles.listItem}>{t("insight3")}</li>
          <li style={baseStyles.listItem}>{t("insight4")}</li>
          <li style={baseStyles.listItem}>{t("insight5")}</li>
        </ul>
      </section>

      {/* Understanding Fat Types */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("fatTypesTitle")}</h2>
        <h3 style={{...h2Style, fontSize: '1.3rem', marginTop: '1.5rem'}}>{t("unsaturatedFatsTitle")}</h3>
        <p style={paragraphStyle}>{t("unsaturatedFatsParagraph")}</p>
        <ul style={baseStyles.list}>
          <li style={baseStyles.listItem}>{t("unsaturatedSource1")}</li>
          <li style={baseStyles.listItem}>{t("unsaturatedSource2")}</li>
          <li style={baseStyles.listItem}>{t("unsaturatedSource3")}</li>
          <li style={baseStyles.listItem}>{t("unsaturatedSource4")}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: '1.3rem', marginTop: '1.5rem'}}>{t("saturatedFatsTitle")}</h3>
        <p style={paragraphStyle}>{t("saturatedFatsParagraph")}</p>

        <h3 style={{...h2Style, fontSize: '1.3rem', marginTop: '1.5rem'}}>{t("transFatsTitle")}</h3>
        <p style={paragraphStyle}>{t("transFatsParagraph")}</p>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t("faq1Question")}</h3>
          <p style={baseStyles.faqAnswer}>{t("faq1Answer")}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t("faq2Question")}</h3>
          <p style={baseStyles.faqAnswer}>{t("faq2Answer")}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t("faq3Question")}</h3>
          <p style={baseStyles.faqAnswer}>{t("faq3Answer")}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t("faq4Question")}</h3>
          <p style={baseStyles.faqAnswer}>{t("faq4Answer")}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={baseStyles.faqQuestion}>{t("faq5Question")}</h3>
          <p style={baseStyles.faqAnswer}>{t("faq5Answer")}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <div style={baseStyles.reference}>
          <a href="https://www.vedantu.com/calculator/fat-intake" target="_blank" rel="noopener noreferrer" style={baseStyles.referenceLink}>
            {t("reference1")}
          </a>
        </div>
        <div style={baseStyles.reference}>
          <a href="https://kefirlab.com/fat-intake-calculator/" target="_blank" rel="noopener noreferrer" style={baseStyles.referenceLink}>
            {t("reference2")}
          </a>
        </div>
        <div style={baseStyles.reference}>
          <a href="https://www.healthline.com/nutrition/how-much-fat-to-eat" target="_blank" rel="noopener noreferrer" style={baseStyles.referenceLink}>
            {t("reference3")}
          </a>
        </div>
        <div style={baseStyles.reference}>
          <a href="https://zoe.com/learn/how-many-grams-of-fat-per-day" target="_blank" rel="noopener noreferrer" style={baseStyles.referenceLink}>
            {t("reference4")}
          </a>
        </div>
      </section>
    </div>
  );
}